// components/ApplicationDownloadNote.tsx
import { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { SuccessToast, ErrorToast } from "../toastMessage";
import { generateFilledPDFDocument } from "./DocumentGenerator";
import { useSelectedApplication } from "./ApplicationFrom.ctx";

const getTranslation = (key: string, language: string): string => {
  const translations: any = {
    english: {
      note: "Note",
      applicants_download_note:
        "Applicants are required to download the application form and submit a duly signed hard copy, along with all requisite documents, at the Nagar Panchayat office. At the time of submitting this online form, applicants need to upload the signed Office Copy (O.C.) of the application and the receipt of the scrutiny fee/payment.",
      download_form: "Download Form",
      downloading: "Downloading...",
      save_application_first: "Please save the application first",
    },
    hindi: {
      note: "नोट",
      applicants_download_note:
        "आवेदकों को आवेदन पत्र डाउनलोड कर विधिवत हस्ताक्षरित हार्ड कॉपी तथा सभी आवश्यक दस्तावेजों सहित नगर पंचायत कार्यालय में जमा करना अनिवार्य है। ऑनलाइन आवेदन जमा करते समय आवेदकों को आवेदन की हस्ताक्षरित कार्यालय प्रति (Office Copy – O.C.) तथा जांच / स्क्रूटनी शुल्क भुगतान की रसीद अपलोड करना आवश्यक है।",
      download_form: "फॉर्म डाउनलोड करें",
      downloading: "डाउनलोड हो रहा है...",
      save_application_first: "कृपया पहले आवेदन सहेजें",
    },
    marathi: {
      note: "सूचना",
      applicants_download_note:
        "अर्जदारांनी अर्जाचा नमुना डाउनलोड करून त्याची विधिवत स्वाक्षरी केलेली हार्ड कॉपी तसेच सर्व आवश्यक कागदपत्रांसह नगर पंचायत कार्यालयात सादर करणे आवश्यक आहे. ऑनलाईन अर्ज सादर करताना अर्जदारांनी अर्जाची स्वाक्षरी केलेली कार्यालयीन प्रत (Office Copy – O.C.) तसेच तपासणी शुल्क / फी भरल्याची पावती अपलोड करणे बंधनकारक आहे.",
      download_form: "फॉर्म डाउनलोड करा",
      downloading: "डाउनलोड होत आहे...",
      save_application_first: "कृपया प्रथम अर्ज जतन करा",
    },
  };

  return translations[language]?.[key] || translations.english[key];
};

export function ApplicationDownloadNote({ getValues, defaults }: any) {
  const language = useLanguage();
  const application = useSelectedApplication();
  const [loading, setLoading] = useState(false);

  const hasBeenSaved = !!application?.id;

  const handleDownload = async () => {
    try {
      setLoading(true);

      const data = {
        ...defaults,
        ...getValues(),
        applicationId: application?.applicationId,
        id: application?.id,
      };

      const blob = await generateFilledPDFDocument(data);
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Application_Form_${application?.applicationId || "Draft"}.pdf`;
      link.click();

      URL.revokeObjectURL(url);
      SuccessToast("Form downloaded successfully!");
    } catch (e) {
      ErrorToast("Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="mx-auto max-w-[800px] mt-6 mb-6">
        <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
          <p className="text-sm text-gray-700">
            <strong>{getTranslation("note", language)}:</strong>{" "}
            {getTranslation("applicants_download_note", language)}
          </p>
        </div>
      </div>

      <div className="flex justify-center mb-6">
        <button
          type="button"
          disabled={!hasBeenSaved || loading}
          onClick={handleDownload}
          className="bg-blue-600 px-6 py-2 rounded-full text-white font-bold disabled:bg-gray-300"
          title={
            !hasBeenSaved
              ? getTranslation("save_application_first", language)
              : ""
          }
        >
          {loading
            ? getTranslation("downloading", language)
            : getTranslation("download_form", language)}
        </button>
      </div>
    </>
  );
}